import { useEffect } from 'react';

/**
 * PageMeta — declarative page-level meta tag injector.
 *
 * Sets <title>, <meta description>, canonical, Open Graph and Twitter card
 * tags via direct DOM mutation inside useEffect. No react-helmet dep needed.
 *
 * Tags are *upserted* by attribute selector so multiple PageMeta mounts on
 * the same route don't duplicate. On unmount we intentionally leave the tags
 * in place so the previous page's tags are simply overwritten by the next
 * page's PageMeta (mirrors how SeoPageRenderer already mutates document.title).
 *
 * Usage:
 *   <PageMeta
 *     title="About Us | ACF Tourism"
 *     description="23+ years of trusted travel expertise..."
 *     canonicalPath="/about-us"
 *     ogImage="/acf-tourism-og.jpg"
 *   />
 */
const upsertMeta = (selector, attrName, attrValue, content) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const absoluteImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://acftourism.com';
  return `${origin}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
};

const PageMeta = ({
  title,
  description,
  canonicalPath,
  ogImage = '/acf-tourism-og.jpg',
  ogType = 'website',
  noIndex = false,
}) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', 'name', 'description', description);
      upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
      upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
      upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    }
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', ogType);
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    const ogImageUrl = absoluteImageUrl(ogImage);
    if (ogImageUrl) {
      upsertMeta('meta[property="og:image"]', 'property', 'og:image', ogImageUrl);
      upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImageUrl);
    }
    if (canonicalPath) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const fullUrl = `${origin}${canonicalPath}`;
      upsertLink('canonical', fullUrl);
      upsertMeta('meta[property="og:url"]', 'property', 'og:url', fullUrl);
    }
    upsertMeta(
      'meta[name="robots"]',
      'name',
      'robots',
      noIndex ? 'noindex, nofollow' : 'index, follow',
    );
  }, [title, description, canonicalPath, ogImage, ogType, noIndex]);

  return null;
};

export default PageMeta;
